import { Box, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { LutiqLogo } from '../components/common/LutiqLogo';

/**
 * The centred logo + eyebrow + heading card every signed-OUT account page
 * renders inside (`/accept-invite`, `/reset-password`).
 *
 * Extracted verbatim from `AcceptInvitePage` so the Scalekit branches added in
 * US-009 sit in the same frame as the Keycloak ones rather than re-deriving it.
 * The emitted DOM is byte-identical to what `AcceptInvitePage` rendered before
 * the extraction — that page's existing tests are the check.
 */
export type AuthPageShellProps = {
  eyebrow: string;
  title: string;
  /** Optional line under the heading, e.g. "Continue as someone@example.com". */
  subtitle?: ReactNode;
  children: ReactNode;
};

const AuthPageShell = ({ eyebrow, title, subtitle, children }: AuthPageShellProps) => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 2,
      bgcolor: 'background.default',
    }}
  >
    <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, maxWidth: 520, width: '100%' }}>
      <Stack spacing={3}>
        <LutiqLogo height={28} />
        <Box>
          <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700 }}>
            {eyebrow}
          </Typography>
          <Typography variant="h4" sx={{ mt: 1 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {children}
      </Stack>
    </Paper>
  </Box>
);

export default AuthPageShell;
